import { Component, effect, input, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { InputComponent } from '../../../../shared/ui/input/input.component';
import type { CustomFieldRawValue, CustomFieldViewModel } from '../../models/custom-field.model';
import {
  formatNumberFieldForDisplayPtBr,
  numberInputMaskDecimalPlaces,
  parseDigitsAsMaskedDecimal,
  parseFlexibleNumberString,
  resolveNumberFieldDecimalPlaces,
  roundToDecimalPlaces,
  shouldPreserveLeadingZeroIntegerRaw,
} from '../../utils/number-field.util';

@Component({
  selector: 'app-cf-number-field',
  standalone: true,
  imports: [InputComponent, FormsModule],
  template: `
    @if (isCurrency()) {
      <label class="nf-shell">
        @if (showLabel()) {
          <span class="nf-label">{{ field().name }}</span>
        }
        <div class="nf-row">
          <input
            class="nf-input"
            type="text"
            inputmode="numeric"
            [required]="field().required"
            [readOnly]="field().readOnly"
            [attr.aria-label]="field().name"
            [placeholder]="resolvedPlaceholder()"
            [ngModel]="currencyEditText()"
            (ngModelChange)="onCurrencyModelChange($event)"
            (blur)="onCurrencyBlur()"
          />
        </div>
      </label>
    } @else if (field().type === 'NUMBER') {
      <label class="nf-shell">
        @if (showLabel()) {
          <span class="nf-label">{{ field().name }}</span>
        }
        <div class="nf-row">
          <input
            class="nf-input"
            type="text"
            [attr.inputmode]="numberFieldInputMode()"
            [required]="field().required"
            [placeholder]="resolvedPlaceholder()"
            [ngModel]="numberEditText()"
            (ngModelChange)="onNumberModelChange($event)"
            (blur)="onNumberBlur()"
          />
          @if (field().numberShowPercentSuffix) {
            <span class="nf-suffix" aria-hidden="true">%</span>
          }
        </div>
      </label>
    } @else {
      <app-input
        [label]="showLabel() ? field().name : ''"
        [required]="field().required"
        [placeholder]="resolvedPlaceholder()"
        [value]="numberValueSimple()"
        [type]="'number'"
        (valueChange)="onValueChangeSimple($event)"
      ></app-input>
    }
  `,
  styles: [
    `
      .nf-shell {
        display: grid;
        gap: 4px;
      }

      .nf-label {
        font-size: 12px;
        color: var(--faint);
      }

      .nf-row {
        display: flex;
        align-items: center;
        gap: 6px;
        min-width: 0;
      }

      .nf-input {
        flex: 1 1 auto;
        min-width: 0;
        height: 36px;
        border: 1px solid var(--border);
        border-radius: 10px;
        background: var(--bg-surface);
        color: var(--text);
        padding: 0 10px;
        font-size: 13px;
      }

      .nf-suffix {
        flex-shrink: 0;
        font-size: 13px;
        font-weight: 600;
        color: var(--muted);
      }
    `,
  ],
})
export class CfNumberFieldComponent {
  readonly field = input.required<CustomFieldViewModel>();
  readonly value = input<CustomFieldRawValue>(null);
  readonly showLabel = input<boolean>(true);
  readonly valueChanged = output<CustomFieldRawValue>();

  /** Texto local para NUMBER: sincroniza com o valor do pai quando este muda. */
  protected readonly numberEditText = signal<string>('');
  protected readonly currencyEditText = signal<string>('');

  constructor() {
    effect(() => {
      const f = this.field();
      const v = this.value();
      void v;
      if (f.type === 'NUMBER') {
        void f.fieldId;
        void f.numberDecimalPlaces;
        void f.numberShowPercentSuffix;
        this.numberEditText.set(this.formatNumberFromParent());
        return;
      }
      if (f.type === 'CURRENCY') {
        this.currencyEditText.set(this.formatCurrencyFromParent());
      }
    });
  }

  isCurrency(): boolean {
    return this.field().type === 'CURRENCY';
  }

  resolvedPlaceholder(): string {
    const ph = this.field().placeholder?.trim();
    if (ph) return this.field().placeholder ?? '';
    if (this.isCurrency()) return 'R$ 0,00';
    if (this.field().type === 'NUMBER' && this.numberFieldUsesDecimalMask()) {
      const dp = this.maskDecimalPlaces();
      return `0,${'0'.repeat(dp)}`;
    }
    return '';
  }

  /** `numeric` no mobile: só dígitos, coerente com máscara tipo moeda. */
  protected numberFieldInputMode(): 'numeric' | 'decimal' {
    if (this.field().type !== 'NUMBER') return 'decimal';
    return this.numberFieldUsesDecimalMask() ? 'numeric' : 'decimal';
  }

  /**
   * Máscara estável: schema + % não somem quando o valor fica `null`; dígitos só no texto, nunca
   * inferir casas a partir do input parcial.
   */
  private maskDecimalPlaces(): number {
    return numberInputMaskDecimalPlaces(this.field(), this.value());
  }

  private numberFieldUsesDecimalMask(): boolean {
    return this.field().type === 'NUMBER' && this.maskDecimalPlaces() > 0;
  }

  numberValueSimple(): number | null {
    const value = this.value();
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed.length) return null;
      const normalized = trimmed.replace(/\s/g, '').replace(',', '.');
      const parsed = Number(normalized);
      return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
  }

  onValueChangeSimple(value: string | number | null): void {
    if (typeof value === 'number') {
      this.valueChanged.emit(value);
      return;
    }
    if (typeof value === 'string' && value.trim()) {
      const parsed = Number(value);
      this.valueChanged.emit(Number.isNaN(parsed) ? null : parsed);
      return;
    }
    this.valueChanged.emit(null);
  }

  protected onNumberModelChange(raw: string): void {
    const f = this.field();
    const maskDp = this.maskDecimalPlaces();
    if (maskDp <= 0) {
      this.numberEditText.set(raw);
      return;
    }

    const { value, digits } = parseDigitsAsMaskedDecimal(raw, maskDp);
    if (value == null || !digits.length) {
      this.numberEditText.set('');
      this.valueChanged.emit(null);
      return;
    }
    this.numberEditText.set(
      formatNumberFieldForDisplayPtBr(value, maskDp, {
        percentSuffix: f.numberShowPercentSuffix === true,
      })
    );
    this.valueChanged.emit(value);
  }

  protected onNumberBlur(): void {
    const f = this.field();
    const raw = this.numberEditText().trim();
    const maskDp = this.maskDecimalPlaces();

    if (!raw.length) {
      this.valueChanged.emit(null);
      this.numberEditText.set('');
      return;
    }

    if (maskDp > 0) {
      const { value } = parseDigitsAsMaskedDecimal(raw, maskDp);
      if (value == null) {
        this.valueChanged.emit(null);
        this.numberEditText.set('');
        return;
      }
      this.valueChanged.emit(value);
      this.numberEditText.set(
        formatNumberFieldForDisplayPtBr(value, maskDp, {
          percentSuffix: f.numberShowPercentSuffix === true,
        })
      );
      return;
    }

    if (shouldPreserveLeadingZeroIntegerRaw(raw, maskDp)) {
      this.valueChanged.emit(raw);
      this.numberEditText.set(raw);
      return;
    }

    const parsed = parseFlexibleNumberString(raw);
    if (parsed == null || !Number.isFinite(parsed)) {
      this.valueChanged.emit(null);
      this.numberEditText.set('');
      return;
    }
    const blurDp = resolveNumberFieldDecimalPlaces(f, raw);
    const rounded = roundToDecimalPlaces(parsed, blurDp);
    this.valueChanged.emit(rounded);
    this.numberEditText.set(
      formatNumberFieldForDisplayPtBr(rounded, blurDp, {
        percentSuffix: f.numberShowPercentSuffix === true,
      })
    );
  }

  protected onCurrencyModelChange(raw: string): void {
    const digitsOnly = raw.replace(/\D+/g, '');
    if (!digitsOnly.length) {
      this.currencyEditText.set('');
      this.valueChanged.emit(null);
      return;
    }

    const parsed = Number(digitsOnly) / 100;
    if (!Number.isFinite(parsed)) {
      this.currencyEditText.set('');
      this.valueChanged.emit(null);
      return;
    }

    const normalized = this.normalizeCurrency(parsed);
    this.currencyEditText.set(this.formatCurrency(normalized));
    this.valueChanged.emit(normalized);
  }

  protected onCurrencyBlur(): void {
    const normalized = this.parseCurrency(this.currencyEditText());
    this.valueChanged.emit(normalized);
    this.currencyEditText.set(normalized == null ? '' : this.formatCurrency(normalized));
  }

  private formatNumberFromParent(): string {
    const f = this.field();
    const v = this.value();
    const dp = numberInputMaskDecimalPlaces(f, v);
    if (typeof v === 'string' && shouldPreserveLeadingZeroIntegerRaw(v, dp)) {
      return v.trim().replace(/\s+/g, '');
    }
    let n: number | null = null;
    if (typeof v === 'number' && Number.isFinite(v)) n = v;
    else if (typeof v === 'string' && v.trim().length > 0) n = parseFlexibleNumberString(v.trim());
    if (n == null || !Number.isFinite(n)) return '';
    const rounded = roundToDecimalPlaces(n, dp);
    return formatNumberFieldForDisplayPtBr(rounded, dp, {
      percentSuffix: f.numberShowPercentSuffix === true,
    });
  }

  private formatCurrencyFromParent(): string {
    const numeric = this.numberValueSimple();
    return numeric == null ? '' : this.formatCurrency(numeric);
  }

  private parseCurrency(value: string | number | null): number | null {
    if (typeof value === 'number') return this.normalizeCurrency(value);
    if (typeof value !== 'string' || value.trim().length === 0) return null;

    const digitsOnly = value.replace(/\D+/g, '');
    if (!digitsOnly) return null;

    const parsed = Number(digitsOnly) / 100;
    if (!Number.isFinite(parsed)) return null;
    return this.normalizeCurrency(parsed);
  }

  private normalizeCurrency(value: number): number {
    return Math.round(value * 100) / 100;
  }

  private formatCurrency(value: number): string {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  }
}
